import { nanoid } from 'nanoid';
import { inject, ref, watch } from '@vue/composition-api';
import useEntity from '../_basic';
import useSchema from './index';
import useGroups from '../users/groups';
import schemaContextMenu from '@/router/routes/schemas/item/entityActions';
import sortBy from 'lodash.sortby';
import downloadBlob from '@/utils/downloadBlob';
import checkNull from '@/utils/checkNull';
import { useLazyQuery } from '@vue/apollo-composable';
import schemaGql from '@/api/schemas/item.graphql';
import cloneDeep from 'lodash.clonedeep';
import useFragmentActions from '@/api/schemas/useFragmentActions';
import hideIfEmpty from '../../utils/hideIfEmpty';
import { isJson } from '@/utils/getCurrentType';

export default (props, context) => {
  const entityComposition = useEntity(props, context);
  const { schema } = useSchema(false);
  const { groups } = useGroups;
  const modalComposition = inject('modalComposition');

  const entity = ref(null);
  let schemaLoaded = ref({});
  const isEnabled = ref(false);
  const groupPropertieSelect = ref([]);
  const propertyTypes = ref([]);

  const lazyQuery = useLazyQuery(
    schemaGql.load,
    {
      id: props.id
    },
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only'
    }
  );

  const fragmentActions = useFragmentActions(props, context, {
    entity,
    lazyQuery
  });

  const loadPropertyTypes = async () => {
    const t = await schema.getPropertyTypes();

    propertyTypes.value = t.data.propertyTypes;
  };

  loadPropertyTypes();
  lazyQuery.onResult(queryResult => {
    loadData(queryResult);
  });

  const subscribeToUpdate = () => {
    lazyQuery.subscribeToMore({
      document: schemaGql.subscribeSchema,
      variables: {
        topic: `schemas:${localStorage.getItem('tokenId')}`,
        filterA: {
          id: [props.id]
        }
      },
      updateQuery: (previousResult, { subscriptionData }) => {
        let clone = cloneDeep(previousResult);
        const relatedNode = subscriptionData.data?.Schemas?.relatedNode;
        const eventType = subscriptionData.data?.Schemas?.event;
        const typeName = relatedNode?.__typename;

        if (eventType === 'insert') {
          if (typeName === 'SchemaProperty') {
            clone.schema.schemaProperties.push(relatedNode);
          }

          if (typeName === 'SchemaControl') {
            clone.schema.schemaControls.push(relatedNode);
          }
          return clone;
        }
      }
    });
  };

  const humanReadableFlag = (nonHuman, schemaProperties) => {
    if (!schemaProperties.length) {
      return nonHuman;
    } else {
      const associateProperty = schemaProperties.filter(
        item => item.property === nonHuman
      )[0];
      if (associateProperty.description) {
        return associateProperty.description;
      }
      return associateProperty.property;
    }
  };

  const loadData = async schemaItem => {
    schemaLoaded.value = schemaItem;

    const createTags = tags => {
      let tagsInner = [];
      if (tags && Array.isArray(tags)) {
        tags.forEach(item => {
          tagsInner.push({
            id: nanoid(),
            title: item
          });
        });
      }
      if (tagsInner.length) {
        return {
          id: nanoid(),
          subheader: 'Tags',
          contextMenu: [],
          properties: {
            chips: tagsInner
          }
        };
      }
      return null;
    };

    const groupFragments = schemataByParentSchemaId => {
      let list = [];
      schemataByParentSchemaId.forEach(item => {
        list.push({
          actionClickElement: () => {
            context.root.$router.push({
              name: 'schemasTableComponent',
              params: { id: item.id }
            });
          },
          contextMenu: fragmentActions.getActions(item),
          id: item.id,
          title: item.name,
          subtitle: ``,
          raw: item,
          icon: {
            name: 'mdi-card-bulleted',
            color: '#fff',
            bg: '#333'
          },
          isHoverAction: true
        });
      });
      return list;
    };

    const groupProperties = object => {
      const list = [];
      const groupNames = object.data.schema.schemaProperties.map(
        item => item.groupName
      );
      const unicGroupName = new Set(groupNames);

      groupPropertieSelect.value = Array.from(unicGroupName);

      const createPropertiesGroups = () => {
        unicGroupName.forEach(item => {
          let allInGroup = [];
          object.data.schema.schemaProperties.forEach(itemNested => {
            if (itemNested.groupName === item) {
              let isObject = isJson(itemNested.type.id);
              let defaultValue = itemNested.defaultValue;

              if (isObject) {
                defaultValue = itemNested.defaultValue
                  ? JSON.stringify(itemNested.defaultValue)
                  : 'n/a';
              }
              allInGroup.push({
                id: nanoid(),
                title: `${humanReadableFlag(
                  itemNested.property,
                  object.data.schema.schemaProperties
                )}: ${itemNested?.type ? itemNested.type.name : 'n/a'}`,
                subtitle: `Default: ${checkNull(defaultValue)}`,
                raw: itemNested,
                icon: '',
                isHoverAction: true
              });
            }
          });

          allInGroup = sortBy(allInGroup, 'title');

          const preInterface = {
            id: nanoid(),
            subheader: item,
            contextMenu: [
              {
                id: 'edit-schema-property',
                text: 'Edit',
                icon: 'mdi-pencil',
                _enabled: true
              },
              {
                id: 'apply-default-value',
                text: 'Apply default value',
                icon: 'mdi-history',
                _enabled: true
              },
              {
                id: 'copy',
                text: 'Copy',
                icon: 'mdi-image-filter-none',
                _enabled: true
              },
              {
                id: 'delete-schema-property',
                text: 'Delete',
                icon: 'mdi-delete',
                _enabled: true
              },
              {
                id: 'show-info-schema-property',
                text: 'Info',
                icon: 'mdi-information',
                _enabled: true
              }
            ],
            properties: [
              ...allInGroup,
              {
                id: nanoid(),
                type: 'additionalItem',
                raw: item,
                actionName: 'add-schema-property',
                title: 'Add property',
                icon: entityComposition.createIcon('mdi-plus')
              }
            ]
          };

          list.push(preInterface);
        });
      };
      createPropertiesGroups();

      return sortBy(list, 'subheader');
    };

    const groupControls = object => {
      const list = [];
      const humanReadableFlag = nonHuman => {
        if (!object.data.schema) {
          return nonHuman;
        } else {
          const associateProperty = object.data.schema.schemaControls.filter(
            item => item.argument === nonHuman
          )[0];
          if (associateProperty.description) {
            return associateProperty.description;
          }
          return associateProperty.argument;
        }
      };

      object.data.schema.schemaControls.forEach(itemNested => {
        if (itemNested.rpc === itemNested.argument) {
          list.push({
            id: nanoid(),
            title: humanReadableFlag(itemNested.argument),
            subtitle: itemNested.typeSpec || 'n/a',
            raw: itemNested,
            contextMenu: [
              {
                id: 'edit-control-scheme',
                text: 'Edit',
                icon: 'mdi-pencil',
                _enabled: true
              },
              {
                id: 'controls-parameters-change',
                text: 'Edit parameters',
                icon: 'mdi-cog',
                _enabled: true
              },
              {
                id: 'show-info-schema-property',
                text: 'Info',
                icon: 'mdi-information',
                _enabled: true
              }
              // {
              //   id: 'delete-schema-control',
              //   text: 'Delete',
              //   icon: 'mdi-delete',
              //   handleAction: () =>
              //     schemaControlActions.deleteSchemaControl(
              //       itemNested,
              //       lazyQuery.restart
              //     ),
              //   _enabled: true
              // }
            ],
            icon: '',
            isHoverAction: true
          });
        }
      });
      // const createPropertiesGroups = () => {
      //   unicGroupName.forEach(item => {
      //     let allInGroup = [];
      //     object.data.schema.schemaControls.forEach(itemNested => {
      //       if (itemNested.rpc === item) {
      //         allInGroup.push({
      //           id: nanoid(),
      //           title: humanReadableFlag(itemNested.argument),
      //           subtitle: itemNested.type || 'n/a',
      //           raw: itemNested,
      //           icon: '',
      //           isHoverAction: true
      //         });
      //       }
      //     });
      //
      //     allInGroup = sortBy(allInGroup, 'title');
      //     if (item) {
      //       const preInterface = {
      //         id: nanoid(),
      //         subheader: item,
      //         contextMenu: [
      //           {
      //             id: 'edit-control-scheme',
      //             text: 'Edit',
      //             icon: 'mdi-pencil',
      //             _enabled: true
      //           },
      //           {
      //             id: 'controls-parameters-change',
      //             text: 'Change parameters',
      //             icon: 'mdi-cog',
      //             _enabled: true
      //           },
      //           {
      //             id: 'copy',
      //             text: 'Copy',
      //             icon: 'mdi-image-filter-none',
      //             _enabled: true
      //           }
      //         ],
      //         properties: {
      //           ...allInGroup
      //         }
      //       };
      //       list.push(preInterface);
      //     }
      //   });
      // };
      // createPropertiesGroups();

      // schemataByParentSchemaId.forEach(item => {
      //   list.push({
      //     actionClickElement: () => {
      //       context.root.$router.push({
      //         name: 'schemasTableComponent',
      //         params: { id: item.id }
      //       });
      //     },
      //     contextMenu: fragmentActions.getActions(item),
      //     id: item.id,
      //     title: item.name,
      //     subtitle: ``,
      //     raw: item,
      //     icon: {
      //       name: 'mdi-card-bulleted',
      //       color: '#fff',
      //       bg: '#333'
      //     },
      //     isHoverAction: true
      //   });
      // });
      return sortBy(list, 'subheader');
    };

    const changeEnable = item => {
      if (item.id === 'enable-disable') {
        if (schemaLoaded.value.data.schema.enabled) {
          item.text = 'Disable';
        } else {
          item.text = 'Enable';
        }
      }
    };

    schemaContextMenu.forEach(item => {
      changeEnable(item);
    });

    isEnabled.value = schemaLoaded.value.data.schema.enabled;

    const getPicture = () => {
      if (schemaLoaded.value.data.schema.mPicture) {
        return `${window.__pixelConfig.APP_MEDIA_SERVER}/download/${
          schemaLoaded.value.data.schema.mPicture
        }/${localStorage.getItem('authToken')}`;
      } else {
        return '/img/datascheme.jpg';
      }
    };
    entity.value = {
      id: schemaLoaded.value.data.schema.id,
      title: schemaLoaded.value.data.schema.name,
      enabled: schemaLoaded.value.data.schema.enabled,
      image: getPicture(),
      imageFallback: '/img/datascheme.jpg',
      contextMenu: schemaContextMenu,
      tabsMeta: [
        {
          id: 'general',
          title: 'general',
          type: 'properties'
        },
        {
          id: 'properties',
          title: 'properties',
          type: 'properties'
        },
        {
          id: 'controls',
          title: 'controls',
          type: 'list'
        },
        {
          id: 'fragments',
          title: 'fragments',
          type: 'list'
        }
      ],
      tabs: [
        {
          items: [
            {
              id: schemaLoaded.value.data.schema.id,
              subheader: 'Service',
              contextMenu: [],
              properties: [
                {
                  id: nanoid(),
                  subtitle: '',
                  title: `Owner: ${checkNull(
                    schemaLoaded.value.data.schema?.userByApplicationOwner
                      ?.login
                  )}`,
                  icon: {
                    name: 'mdi-security'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  title: `Type: ${schemaLoaded.value.data.schema.schemaType.toLowerCase()}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-format-list-bulleted-type'
                  },
                  isHoverAction: false
                },
                schemaLoaded.value.data.schema.parentSchemaId
                  ? {
                      id: nanoid(),
                      title: schemaLoaded.value.data.schema.parentSchema.name,
                      subtitle: '',
                      icon: {
                        name: 'mdi-link'
                      },
                      isHoverAction: false
                    }
                  : null,
                {
                  id: nanoid(),
                  title: `Object count: ${schemaLoaded.value.data?.schema?.objectsCount}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-file-tree'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  title: schemaLoaded.value.data?.schema.id,
                  subtitle: '',
                  icon: {
                    name: 'mdi-identifier'
                  },
                  isHoverAction: false,
                  contextMenu: []
                }
              ]
            },
            {
              id: nanoid(),
              subheader: 'Contact info',
              contextMenu: [],
              properties: [
                {
                  id: nanoid(),
                  title: `Author: ${checkNull(
                    schemaLoaded.value.data.schema.mAuthor
                  )}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-account'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  subtitle: '',
                  title: `Support: ${checkNull(
                    schemaLoaded.value.data.schema.mEmail
                  )}`,
                  icon: {
                    name: 'mdi-email'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  subtitle: '',
                  title: `Version: ${checkNull(
                    schemaLoaded.value.data.schema.mVersion
                  )}`,
                  icon: {
                    name: 'mdi-information'
                  },
                  isHoverAction: false
                },
                {
                  id: nanoid(),
                  subtitle: '',
                  title: `Manufacturer: ${checkNull(
                    schemaLoaded.value.data.schema.mManufacturer
                  )}`,
                  icon: {
                    name: 'mdi-domain'
                  },
                  isHoverAction: false
                }
              ]
            },
            createTags(schemaLoaded.value.data.schema.mTags),
            {
              id: nanoid(),
              subheader: 'Access',
              contextMenu: [
                {
                  id: 'edit-rights',
                  text: 'Edit',
                  icon: 'mdi-pencil',
                  _enabled: true
                },
                {
                  text: 'Copy',
                  icon: 'mdi-image-filter-none',
                  _enabled: true
                },
                {
                  id: 'show-history',
                  text: 'Show history',
                  icon: 'mdi-history'
                }
              ],
              properties: [
                {
                  id: nanoid(),
                  raw: {
                    ...schemaLoaded.value.data.schema.userGroupByEditorgroup,
                    type: 'editorgroup'
                  },
                  title: `Editors: ${schemaLoaded.value.data.schema
                    .userGroupByEditorgroup?.groupName || null}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-pencil'
                  },
                  dropdown: [],
                  isHoverAction: true
                },
                {
                  id: nanoid(),
                  raw: {
                    ...schemaLoaded.value.data.schema.userGroupByUsergroup,
                    type: 'usergroup'
                  },
                  title: `Users: ${schemaLoaded.value.data.schema
                    .userGroupByUsergroup?.groupName || null}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-play-circle-outline'
                  },
                  dropdown: [],
                  isHoverAction: true
                },
                {
                  id: nanoid(),
                  raw: {
                    ...schemaLoaded.value.data.schema.userGroupByReadergroup,
                    type: 'readergroup'
                  },
                  title: `Readers: ${schemaLoaded.value.data.schema
                    .userGroupByReadergroup?.groupName || null}`,
                  subtitle: '',
                  icon: {
                    name: 'mdi-eye'
                  },
                  dropdown: [],
                  isHoverAction: true
                }
              ]
            },
            ...hideIfEmpty(schemaLoaded.value.data.schema.description, [
              {
                id: nanoid(),
                subheader: 'Description',
                contextMenu: [],
                properties: [
                  {
                    id: nanoid(),
                    title: checkNull(
                      schemaLoaded.value.data.schema.description
                    ),
                    icon: 'none',
                    isHoverAction: false
                  }
                ]
              }
            ])
          ],
          additionalItems: []
        },
        {
          items: groupProperties(schemaLoaded.value),
          additionalItems: groupProperties(schemaLoaded.value).length
            ? []
            : [
                {
                  id: nanoid(),
                  type: 'additionalItem',
                  actionName: 'add-schema-property',
                  title: 'Add property',
                  icon: entityComposition.createIcon('mdi-plus')
                }
              ]
        },
        {
          items: groupControls(schemaLoaded.value),
          additionalItems: [
            {
              actionName: 'add-schema-control',
              title: 'Add control',
              icon: entityComposition.createIcon('mdi-plus')
            }
          ]
        },
        {
          items: groupFragments(
            schemaLoaded.value.data.schema.schemataByParentSchemaId
          ),
          additionalItems: [
            {
              id: nanoid(),
              type: 'additionalItem',
              title: 'Add existing schema',
              handleAction: async () => {
                const component = await import(
                  '@/components/app-entity/modals/AddSchemaFragment'
                );
                modalComposition.handleOpen(component, { id: props.id });
              },
              icon: entityComposition.createIcon('mdi-plus')
            }
          ]
        }
      ]
    };

    schema.loading.value = false;
  };

  const getTargetId = () => {
    return props?.id || context.root.$route.params.id;
  };

  const getRights = () => {
    let propName = entityComposition.checkRights();

    if (propName === 'editorgroup') {
      return entityComposition.dropdownRights.value.map(item => ({
        ...item,
        disabled: item.name === 'Nobody'
      }));
    }
    return entityComposition.dropdownRights.value.map(item => ({
      ...item,
      disabled: false
    }));
  };

  const changeRights = e => {
    entityComposition.showPopup.value = false;
    let propName = entityComposition.checkRights();
    if (propName) {
      schema
        .update(getTargetId(), {
          [propName]: entityComposition.popupValue.value.id
        })
        .then(() => {
          // loadData(getTargetId());
        })
        .finally(() => {
          entityComposition.handleClose();
        });
    }
  };
  const changeGroupRights = data => {
    if (data) {
      schema
        .update(getTargetId(), data)
        .then(() => {
          // loadData(getTargetId());
        })
        .finally(() => {
          entityComposition.handleClose();
        });
    }
  };

  watch(
    () => context.root.$route.query.timestamp,
    value => {
      if (context.root.$route.query.timestamp) {
        // loadData(getTargetId());
      }
    }
  );

  watch(
    () => entityComposition.typeAction.value,
    value => {
      switch (value) {
        case 'edit-rights':
          groups.load().then(res => {
            entityComposition.dropdownRights.value = res.map(item => ({
              ...item,
              disabled: item.isSystem
            }));

            if (entityComposition.currentContext.value.raw) {
              res.forEach(item => {
                if (item.id === entityComposition.currentContext.value.raw.id) {
                  entityComposition.popupValue.value = item;
                }
              });
            }
          });
          break;
        case 'enable-disable':
          entityComposition.typeAction.value = '';
          schema
            .update(getTargetId(), {
              enabled: !isEnabled.value
            })
            .then(() => {
              // loadData(getTargetId());
            })
            .then(() => {
              schema.loading.value = false;
              // loadData(getTargetId());
            });
          break;
        case 'export-json':
          entityComposition.typeAction.value = '';
          schema
            .exportJson(getTargetId())
            .then(res => {
              const schema = JSON.parse(res.data.exportSchema).schema;
              downloadBlob(
                res.data.exportSchema,
                `${schema.name}_v${schema.m_version}.json`,
                'application/json'
              );
            })
            .finally(() => {
              schema.loading.value = false;
            });
          break;
        case 'bump-version':
          entityComposition.typeAction.value = '';
          schema
            .bumpVersion([getTargetId()])
            .then(res => {})
            .finally(() => {
              schema.loading.value = false;
            });
      }
    }
  );

  watch(
    () => props.id,
    value => {
      lazyQuery.variables.value.id = value;
      subscribeToUpdate();
    }
  );
  return {
    ...entityComposition,
    entity,
    loadData,
    lazyQuery,
    loading: schema.loading,
    loadSchema: lazyQuery.load,
    subscribeToUpdate,
    schemaLoaded,
    changeRights,
    humanReadableFlag,
    groupPropertieSelect,
    changeGroupRights,
    getRights,
    propertyTypes
  };
};
